
import { Component, Vue, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { Select, Row, Heading } from '@warrenbrasil/nebraska-web';
import { PositionItem } from './components/PositionItem';
import { HandleSection } from '@/modules/trade/components/HandleSection';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Instrument, getPositions } from '@/modules/trade/services/instruments';
import { ObservableSubscription } from '@apollo/client/utilities/observables/Observable';
import {
  trackInstrumentPositionClick,
  trackPositionOptionClick,
  trackViewAllPositions,
  trackPositionsRetryClick,
  trackPositionsRequestError
} from '@/modules/trade/views/TradeView/trackers';
import { logError } from '@/modules/trade/helpers/logs';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { positionsOptionsV1, positionsOptionsV2 } from './data';
import { IPositionOptionItem } from './types';
import { NumberFormatted } from '@/modules/trade/types';

const tradePositionsModule = namespace('tradeModule');

@Component({
  components: {
    Select,
    Row,
    HandleSection,
    Heading,
    PositionItem
  }
})
export default class PositionsList extends Vue {
  @Prop({ type: Function, required: true })
  readonly setPositionSubscription!: () => {};

  @tradePositionsModule.Getter('getPositionSubscription')
  readonly getPositionSubscription!: ObservableSubscription;

  @tradePositionsModule.Getter('getPositions')
  readonly positions!: Instrument[];

  @tradePositionsModule.State('positionsUpdatedAt')
  readonly positionsUpdatedAt!: NumberFormatted;

  @tradePositionsModule.Mutation('setPositionList')
  readonly setPositionList!: Function;

  @tradePositionsModule.Mutation('setPositionsUpdatedAt')
  readonly setPositionsUpdatedAt!: Function;

  readonly NebraskaColors = NebraskaColors;
  error = false;
  showButton = false;
  loading = false;
  selectedPositionOption: IPositionOptionItem | null = null;

  created() {
    this.setInitialPositionOption();
    this.fetchPositionsAndSubscribe();
  }

  destroyed() {
    if (this.getPositionSubscription) {
      this.getPositionSubscription.unsubscribe();
    }
  }

  get isFinOneIntegrationWithTradeV0Enabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_FIN_ONE_INTEGRATION_WITH_TRADE_V0
    );
  }

  get subTitle() {
    if (this.isFinOneIntegrationWithTradeV0Enabled)
      return this.positionsUpdatedAt.formatted;
    return 'Ações, FIIs, ETFs e BDRs';
  }

  get positionsOptions() {
    if (this.isFinOneIntegrationWithTradeV0Enabled) {
      return positionsOptionsV2;
    }
    return positionsOptionsV1;
  }

  setInitialPositionOption() {
    if (this.isFinOneIntegrationWithTradeV0Enabled) {
      this.selectedPositionOption = positionsOptionsV2[0];
    } else {
      this.selectedPositionOption = positionsOptionsV1[0];
    }
  }

  async handleOnRetryClick() {
    trackPositionsRetryClick();
    await this.fetchPositionsAndSubscribe();
  }

  async fetchPositionsAndSubscribe() {
    try {
      this.loading = true;
      this.error = false;

      const data = await getPositions({
        type: 'all',
        page: 1,
        itemsPerPage: 10
      });
      this.setPositionList(data?.items || []);
      this.showButton = data?.items?.length > 0;
      if (data.updatedAt) this.setPositionsUpdatedAt(data.updatedAt);
    } catch (e) {
      logError(e, 'request_positions_error');
      trackPositionsRequestError();
      this.error = true;
    } finally {
      this.loading = false;
      await this.setPositionSubscription();
    }
  }

  handleViewAllClick() {
    trackViewAllPositions();
    this.$router.push({ name: 'tradePositions' });
  }

  onPositionClick(instrument: Instrument) {
    trackInstrumentPositionClick(instrument.symbol, instrument.name || '');
    this.$router.push({
      name: 'tradeInstrument',
      params: { ticker: instrument.symbol }
    });
  }

  handleSelectPositionOption(option: IPositionOptionItem) {
    trackPositionOptionClick(option.label);
    this.selectedPositionOption = option;
  }
}
